import { useSelectedBoxId } from "@modules/box/useBox"
import { word } from "@modules/core/utils/i18n"
import { FavoritesPersonalRecord, PublicProfile } from "@modules/profile/publicProfile/publicProfileTypes"
import { ItemProgram } from "@modules/programs/programTypes"
import { HuProgramMiniCards } from "@modules/ui/components/cards/huProgramMiniCard"
import { HuBioCard } from "@modules/ui/components/huBioCard"
import { HUFavoritePersonalRecordMiniCards } from "@modules/ui/components/huFavoritePersonalRecordMiniCards"
import { HUText } from "@modules/ui/components/huText"
import dayjs from "dayjs"
import { Dumbbell } from "lucide-react"
import { Carousel } from "primereact/carousel"
import { useTheme } from "styled-components"

type Props = {
  publicProfile: PublicProfile
  isBioCollapsible?: boolean
  isBioCollapsed?: boolean
  className?: string
} & React.ComponentProps<"div">

export const PublicProfileDetails: React.FC<Props> = ({
  publicProfile,
  isBioCollapsible = false,
  isBioCollapsed = false,
  className,
  ...props
}) => {
  const defaultClassName = "flex flex-column w-full gap-6 pb-4"
  const theme = useTheme()
  const boxId = useSelectedBoxId()

  const programCardCarrouselTemplate = (program: ItemProgram) => {
    return <HuProgramMiniCards item={program} disabled={!!boxId} />
  }

  const personalRecordCarrouselTemplate = (personalRecord: FavoritesPersonalRecord) => {
    return <HUFavoritePersonalRecordMiniCards item={personalRecord} />
  }

  return (
    <div className={className ?? defaultClassName} {...props}>
      {/* BIO */}
      <HuBioCard
        description={publicProfile.biography}
        age={dayjs().diff(publicProfile.birthDate, "year")}
        weight={publicProfile.weight}
        height={publicProfile.height}
        category={publicProfile.genderCategory}
        job={publicProfile.job}
        style={{
          marginTop: isBioCollapsible ? -50 : 0,
          marginBottom: 20,
          padding: isBioCollapsible ? (isBioCollapsed ? "0px 2rem" : "70px 2rem 2rem 2rem") : "2rem",
          maxHeight: isBioCollapsed ? "0px" : "1000px",
          transition: "all 0.5s ease-in-out",
        }}
      />
      {/* CROSSFIT START DATE */}
      {publicProfile.crossfitStartDate && (
        <div className="flex flex-row gap-2">
          <Dumbbell strokeWidth="1" />
          <div className="flex flex-column gap-1 mt-1">
            <HUText className="uppercase" fontStyle="BS" color={theme.neutralColor600}>
              {word("publicProfile.startDate")}
            </HUText>
            <HUText fontStyle="TS">{dayjs(publicProfile.crossfitStartDate).format("L")}</HUText>
          </div>
        </div>
      )}

      {/* BADGES */}
      {publicProfile.badges && publicProfile.badges.length > 0 && (
        <div>
          <HUText fontStyle="TM">{word("publicProfile.badges")}</HUText>
          <div className="flex flex-wrap mt-2 gap-2">
            {publicProfile.badges.map((badge, index) => (
              <img key={index} src={badge.badge.image.path} alt={badge.badge.name} />
            ))}
          </div>
        </div>
      )}

      {/* PERSONAL RECORDS */}
      {publicProfile.favoritesPersonalRecords.length > 0 && (
        <div>
          <HUText fontStyle="TM">{word("publicProfile.personalRecords")}</HUText>
          <div className="flex flex-wrap gap-2 mt-2">
            <Carousel
              className="w-full"
              showIndicators={false}
              showNavigators={true}
              numVisible={3}
              value={publicProfile.favoritesPersonalRecords}
              itemTemplate={personalRecordCarrouselTemplate}
            />
          </div>
        </div>
      )}

      {/* PROGRAMS */}
      {publicProfile.ownedPrograms && publicProfile.ownedPrograms.length > 0 && (
        <div>
          <HUText fontStyle="TM">{word("publicProfile.userPersonalPrograms")}</HUText>
          <div className="flex flex-wrap gap-2 mt-2">
            <Carousel
              className="w-full"
              showIndicators={false}
              showNavigators={true}
              numVisible={2}
              value={publicProfile.ownedPrograms}
              itemTemplate={programCardCarrouselTemplate}
            />
          </div>
        </div>
      )}
      {/* FOLLOWED PROGRAMS */}
      {publicProfile.followedPrograms && publicProfile.ownedPrograms.length > 0 && (
        <div>
          <HUText fontStyle="TM">{word("publicProfile.followedPrograms")}</HUText>
          <div className="flex flex-wrap gap-2 mt-2">
            <Carousel
              className="w-full"
              showIndicators={false}
              showNavigators={true}
              numVisible={2}
              value={publicProfile.followedPrograms}
              itemTemplate={programCardCarrouselTemplate}
            />
          </div>
        </div>
      )}
    </div>
  )
}
